// src/Components/Broadcast/hooks/useBroadcast.js
import { useState, useCallback } from 'react';
import { sendTextMessage, sendPhotoMessage } from '../services/telegramService';
import { logBroadcast } from '../services/userService';

export const useBroadcast = ({
  targetFilter,
  selectedUsers,
  filteredUsers,
  userList,
  productFilter,
  giftFilter,
  excludedUsers,
  excludedGroups,
  excludedProducts,
  combinationType,
  excludeSentUsers,
  setNotification,
  onHistoryUpdate,
  sentUserIds
}) => {
  const [messageText, setMessageText] = useState('');
  const [messageType, setMessageType] = useState('text');
  const [parseMode, setParseMode] = useState('HTML');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0, failed: [] });
  const [previewData, setPreviewData] = useState({
    text: '',
    type: 'text',
    parseMode: 'HTML',
    recipientCount: 0,
    hasImage: false,
    imagePreview: null
  });
  const [showPreview, setShowPreview] = useState(false);

  // Функция для проверки доступа к курсу
  const checkCourseAccess = useCallback((user, courseId) => {
    if (!user) return false;
    
    if (user.courses && Array.isArray(user.courses)) {
      if (user.courses.includes(courseId)) return true;
    }
    
    if (user.coursesAccess && Array.isArray(user.coursesAccess)) {
      for (const access of user.coursesAccess) {
        if (access && access.id === courseId) return true;
        if (access === courseId) return true;
      }
    }
    
    return false;
  }, []);

  // Функция для проверки доступа к тренингу
  const checkTrainingAccess = useCallback((user, trainingId) => {
    if (!user) return false;
    
    if (user.trainings && Array.isArray(user.trainings)) {
      if (user.trainings.includes(trainingId)) return true;
    }
    
    if (user.trainingsAccess && Array.isArray(user.trainingsAccess)) {
      for (const access of user.trainingsAccess) {
        if (access && access.id === trainingId) return true;
        if (access === trainingId) return true;
      }
    }
    
    return false;
  }, []);

  // Проверка: есть ли доступ к выбранным продуктам
  const hasSelectedProducts = useCallback((user) => {
    if (!productFilter) return false;
    if (!productFilter.courses?.length && !productFilter.trainings?.length) return false;
    
    if (productFilter.courses?.length > 0) {
      for (const courseId of productFilter.courses) {
        if (checkCourseAccess(user, courseId)) return true;
      }
    }
    
    if (productFilter.trainings?.length > 0) {
      for (const trainingId of productFilter.trainings) {
        if (checkTrainingAccess(user, trainingId)) return true;
      }
    }
    
    return false;
  }, [productFilter, checkCourseAccess, checkTrainingAccess]);

  // Проверка: есть ли у пользователя хоть какие-то продукты
  const hasAnyProduct = useCallback((user) => {
    const hasCourse = (user.courses?.length > 0) || (user.coursesAccess?.length > 0);
    const hasTraining = (user.trainings?.length > 0) || (user.trainingsAccess?.length > 0);
    return hasCourse || hasTraining;
  }, []);

  // Проверка: соответствует ли пользователь выбранной комбинации
  const matchesCombination = useCallback((user) => {
    switch (combinationType) {
      case 'none':
        return true;
        
      case 'has_products':
        if ((!productFilter?.courses?.length && !productFilter?.trainings?.length)) {
          return false;
        }
        return hasSelectedProducts(user);
        
      case 'no_products':
        if ((!productFilter?.courses?.length && !productFilter?.trainings?.length)) {
          return false;
        }
        return !hasSelectedProducts(user);
        
      case 'no_products_any':
        return !hasAnyProduct(user);
        
      default:
        return true;
    }
  }, [combinationType, productFilter, hasSelectedProducts, hasAnyProduct]);

  // Получение списка получателей с учетом дополнительных фильтров
  const getFilteredRecipients = useCallback((users) => {
    if (!users || users.length === 0) return [];
    
    let recipients = [...users];
    
    // Применяем комбинированный фильтр
    recipients = recipients.filter(user => matchesCombination(user));
    
    // Исключение пользователей по ID
    if (excludedUsers && excludedUsers.length > 0) {
      recipients = recipients.filter(user => !excludedUsers.includes(user.id));
    }
    
    // Исключение групп
    if (excludedGroups && excludedGroups.length > 0) {
      if (excludedGroups.includes('premium')) {
        recipients = recipients.filter(user => !user.premium);
      }
      if (excludedGroups.includes('free')) {
        recipients = recipients.filter(user => user.premium);
      }
    }
    
    // Исключение по продуктам
    if (excludedProducts && excludedProducts.length > 0) {
      const excludedCourses = excludedProducts
        .filter(key => key.startsWith('course_'))
        .map(key => key.replace('course_', ''));
      
      const excludedTrainings = excludedProducts
        .filter(key => key.startsWith('training_'))
        .map(key => key.replace('training_', ''));
      
      recipients = recipients.filter(user => {
        let hasExcludedAccess = false;
        
        if (excludedCourses.length > 0) {
          for (const courseId of excludedCourses) {
            if (checkCourseAccess(user, courseId)) {
              hasExcludedAccess = true;
              break;
            }
          }
        }
        
        if (!hasExcludedAccess && excludedTrainings.length > 0) {
          for (const trainingId of excludedTrainings) {
            if (checkTrainingAccess(user, trainingId)) {
              hasExcludedAccess = true;
              break;
            }
          }
        }
        
        return !hasExcludedAccess;
      });
    }
    
    // Исключение пользователей, которые уже получали рассылку
    if (excludeSentUsers && sentUserIds && sentUserIds.length > 0) {
      recipients = recipients.filter(user => !sentUserIds.includes(user.id));
    }
    
    return recipients;
  }, [combinationType, productFilter, excludedUsers, excludedGroups, excludedProducts, excludeSentUsers, sentUserIds, matchesCombination, checkCourseAccess, checkTrainingAccess]);

  // Получение итоговых получателей
  const getRecipients = useCallback(() => {
    return getFilteredRecipients(filteredUsers || []);
  }, [filteredUsers, getFilteredRecipients]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        if (setNotification) setNotification('error', 'Пожалуйста, выберите изображение');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        if (setNotification) setNotification('error', 'Изображение не должно превышать 10 МБ');
        return;
      }

      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageRemove = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const insertFormatting = (tag) => {
    const textarea = document.getElementById('message-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = messageText.substring(start, end);
    
    let formattedText = '';
    switch(tag) {
      case 'bold':
        formattedText = parseMode === 'HTML' ? `<b>${selectedText}</b>` : `*${selectedText}*`;
        break;
      case 'italic':
        formattedText = parseMode === 'HTML' ? `<i>${selectedText}</i>` : `_${selectedText}_`;
        break;
      case 'code':
        formattedText = parseMode === 'HTML' ? `<code>${selectedText}</code>` : `\`${selectedText}\``;
        break;
      case 'link':
        const url = prompt('Введите URL ссылки:', 'https://');
        if (url) {
          formattedText = parseMode === 'HTML' 
            ? `<a href="${url}">${selectedText || 'ссылка'}</a>`
            : `[${selectedText || 'ссылка'}](${url})`;
        } else {
          return;
        }
        break;
      case 'list':
        formattedText = selectedText.split('\n').map(line => `• ${line}`).join('\n');
        break;
      default:
        return;
    }
    
    const newText = messageText.substring(0, start) + formattedText + messageText.substring(end);
    setMessageText(newText);
  };

  const saveToHistory = useCallback((recipients, successfullSends, failedSends, sentUserIdsList) => {
    const broadcastRecord = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      text: messageText.substring(0, 100) + (messageText.length > 100 ? '...' : ''),
      type: messageType,
      hasImage: !!imageFile,
      parseMode: parseMode,
      targetFilter: targetFilter,
      combinationType: combinationType,
      totalRecipients: recipients.length,
      successCount: successfullSends.length,
      failedCount: failedSends.length,
      successUsers: successfullSends,
      failedUsers: failedSends.map(f => ({ id: f.id, reason: f.reason })),
      sentUserIds: sentUserIdsList,
      productFilter: productFilter,
      giftFilter: giftFilter,
      excludedUsers: excludedUsers,
      excludedGroups: excludedGroups,
      excludedProducts: excludedProducts
    };

    try {
      const existingHistory = JSON.parse(localStorage.getItem('broadcastHistory') || '[]');
      const newHistory = [broadcastRecord, ...existingHistory].slice(0, 50);
      localStorage.setItem('broadcastHistory', JSON.stringify(newHistory));
      
      if (onHistoryUpdate) {
        onHistoryUpdate(newHistory);
      }
    } catch (e) {
      console.error('Ошибка сохранения истории:', e);
    }
  }, [messageText, messageType, imageFile, parseMode, targetFilter, combinationType, productFilter, giftFilter, excludedUsers, excludedGroups, excludedProducts, onHistoryUpdate]);

  const preparePreview = () => {
    if (!messageText.trim() && !imageFile) {
      if (setNotification) setNotification('warning', 'Добавьте текст сообщения или изображение');
      return;
    }

    const recipients = getRecipients();

    if (recipients.length === 0) {
      if (setNotification) setNotification('warning', 'Нет получателей для рассылки');
      return;
    }

    setPreviewData({
      text: messageText,
      type: messageType,
      parseMode: parseMode,
      recipientCount: recipients.length,
      hasImage: !!imageFile,
      imagePreview: imagePreview
    });
    
    setShowPreview(true);
  };

  const sendMessage = async () => {
    if (!messageText.trim() && !imageFile) {
      if (setNotification) setNotification('warning', 'Добавьте текст сообщения или изображение');
      return;
    }

    let recipients = getRecipients();

    if (recipients.length === 0) {
      if (setNotification) setNotification('warning', 'Нет получателей для рассылки');
      return;
    }

    setSending(true);
    setSendProgress({ sent: 0, total: recipients.length, failed: [] });

    const successfullSends = [];
    const failedSends = [];
    const sentUserIdsList = [];

    for (let i = 0; i < recipients.length; i++) {
      const user = recipients[i];
      
      try {
        let data;
        
        if (imageFile) {
          data = await sendPhotoMessage(user.id, imageFile, messageText, parseMode);
        } else {
          data = await sendTextMessage(user.id, messageText, parseMode);
        }

        if (data.ok) {
          successfullSends.push(user.id);
          sentUserIdsList.push(user.id);
          await logBroadcast(user.id, 'success', messageText, !!imageFile, null, messageType, parseMode);
        } else {
          failedSends.push({ id: user.id, reason: data.description });
          await logBroadcast(user.id, 'failed', messageText, !!imageFile, data.description, messageType, parseMode);
        }

      } catch (error) {
        console.error(`Ошибка отправки пользователю ${user.id}:`, error);
        failedSends.push({ id: user.id, reason: error.message });
        await logBroadcast(user.id, 'failed', messageText, !!imageFile, error.message, messageType, parseMode);
      }

      setSendProgress(prev => ({
        ...prev,
        sent: i + 1,
        failed: failedSends
      }));

      await new Promise(resolve => setTimeout(resolve, 50));
    }

    saveToHistory(recipients, successfullSends, failedSends, sentUserIdsList);

    if (setNotification) {
      setNotification(
        failedSends.length === 0 ? 'success' : failedSends.length === recipients.length ? 'error' : 'warning',
        `Рассылка завершена. Успешно: ${successfullSends.length}, Ошибок: ${failedSends.length}`
      );
    }

    setSending(false);
    handleImageRemove();
    setShowPreview(false);
    
    return { successfullSends, failedSends, sentUserIds: sentUserIdsList };
  };

  return {
    messageText,
    setMessageText,
    messageType,
    setMessageType,
    parseMode,
    setParseMode,
    imageFile,
    imagePreview,
    sending,
    sendProgress,
    previewData,
    setPreviewData,
    showPreview,
    setShowPreview,
    handleImageSelect,
    handleImageRemove,
    insertFormatting,
    preparePreview,
    sendMessage,
    getRecipients
  };
};